// Shared pure helpers for the AI campaign flow.
//
// These were originally defined inline in campaign-draft-preview.js. They are
// now shared so the campaign draft preview and the standalone campaign-template
// editor (ai-campaign-template.js) apply identical normalization and the same
// attack-type / credential-harvesting visibility rules without diverging.
import { collectTemplateCourseIds } from "../create-template/collect-template-course-ids";

export const emptyData = {
  name: "",
  attackVector: "Phishing",
  type: "Simulation Attack",
  users: "0",
  groups: "0",
  groupIds: [],
  groupDetails: [],
  groupTags: [],
  subject: "",
  senderName: "",
  senderTo: "",
  body: "",
  landingPageHtml: "",
  landingPageUrl: "",
  awarenessHtml: "",
  campaignPreviewId: "",
  campaignTemplateId: null,
  campaignTemplateIds: [],
  category: "",
  single: true,
  random: false,
  scheduledAt: new Date(),
  campaignExpiry: "",
  expiryOption: "7 Days",
  customExpiryDate: null,
  executionMode: "Immediately",
  campaignType: "live",
  identifier: "",
  credentialHarvesting: false,
  targetInteractionType: "",
  isAIbased: false,
  sendCampaignOnOldSimulation: false,
  simulationId: 0,
  simulationState: null,
  simulationState1: null,
  simulationState2: null,
  lmsMsgStatus: "",
  delay: 1,
  isRepliedCampaign: false,
  isInstantAwareness: true,
  launchStatus: "draft",
  launchedCampaignId: null,
  courseIds: [],
};

export function coerceBoolean(value, fallback = false) {
  if (value === true || value === false) return value;
  if (value === 1 || value === 0) return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off", ""].includes(normalized)) return false;
  }
  return fallback;
}

export function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function toNumberArray(value) {
  const values = Array.isArray(value) ? value : value == null || value === "" ? [] : [value];
  return [...new Set(values.map(toNumber).filter((number) => number != null))];
}

export function toDate(value, fallback = new Date()) {
  if (value == null || value === "") return fallback;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : date;
}

export function formatDateOnly(value) {
  const date = toDate(value, null);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatDateTime(value) {
  const date = toDate(value, null);
  return date ? date.toISOString() : "";
}

export function addDays(value, days) {
  const date = new Date(toDate(value).getTime());
  date.setDate(date.getDate() + days);
  return date;
}

export function getDayDiff(start, end) {
  const startDate = toDate(start, null);
  const endDate = toDate(end, null);
  if (!startDate || !endDate) return null;

  const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  return Math.round((endDay.getTime() - startDay.getTime()) / 86400000);
}

export const EXPIRY_OPTION_DAYS = {
  "7 Days": 7,
  "15 Days": 15,
};

export function normalizeExpiryOption(value) {
  const option = String(value || "").trim();
  if (option === "Custom") return option;
  return EXPIRY_OPTION_DAYS[option] ? option : "";
}

export function resolveExpiryState(scheduledAt, campaignExpiry, expiryOption) {
  const normalizedOption = normalizeExpiryOption(expiryOption);
  if (normalizedOption === "Custom") {
    return {
      expiryOption: "Custom",
      customExpiryDate: toDate(campaignExpiry, null),
      campaignExpiry: formatDateTime(campaignExpiry),
    };
  }
  if (normalizedOption) {
    return {
      expiryOption: normalizedOption,
      customExpiryDate: null,
      campaignExpiry: formatDateTime(campaignExpiry || addDays(scheduledAt, EXPIRY_OPTION_DAYS[normalizedOption])),
    };
  }

  const diff = getDayDiff(scheduledAt, campaignExpiry);
  if (diff === 15) {
    return { expiryOption: "15 Days", customExpiryDate: null, campaignExpiry: formatDateTime(campaignExpiry) };
  }
  if (diff === 7 || !campaignExpiry) {
    return {
      expiryOption: "7 Days",
      customExpiryDate: null,
      campaignExpiry: formatDateTime(campaignExpiry || addDays(scheduledAt, 7)),
    };
  }
  return {
    expiryOption: "Custom",
    customExpiryDate: toDate(campaignExpiry, null),
    campaignExpiry: formatDateTime(campaignExpiry),
  };
}

export function normalizeAttackType(value) {
  const raw = String(value || "").trim();
  if (raw === "Email Phishing") return "Phishing";
  if (raw === "QR Code") return "QRcode";
  return raw || "Phishing";
}

export function normalizeSimulationType(value) {
  const raw = String(value || "").trim();
  if (raw === "Simulation") return "Simulation Attack";
  return raw || "Simulation Attack";
}

export function normalizeInteractionType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "inbound" || raw === "outbound") return raw;
  return "";
}

export function normalizeFlowType(value) {
  return String(value || "").trim().toLowerCase();
}

export function isEmailFlowType(value) {
  const type = normalizeFlowType(value);
  return type === "email" || type.startsWith("email_");
}

export function isMessageFlowType(value) {
  const type = normalizeFlowType(value);
  return type === "message" || type.startsWith("message_") || ["sms", "whatsapp", "telegram"].includes(type);
}

export function formatAttackTypeLabel(value) {
  const attackType = normalizeAttackType(value);
  return attackType === "QRcode" ? "QR Code" : attackType;
}

export function normalizeAttackTypeOption(value) {
  const attackType = value?.attack_type || value;
  const rawName = String(attackType?.name || attackType?.value || "").trim();
  if (!rawName) return null;
  const name = normalizeAttackType(rawName);
  return {
    id: attackType?.id ?? value?.attackTypeId ?? null,
    value: name,
    label: attackType?.label || formatAttackTypeLabel(name),
    type: attackType?.type || "",
  };
}

export function dedupeAttackTypeOptions(options = []) {
  const seen = new Set();
  return options.filter((option) => {
    if (!option?.value || seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
}

export function normalizeSimulationTypeOption(simulationType) {
  const rawName = String(simulationType?.name || simulationType?.value || "").trim();
  if (!rawName) return null;
  const name = normalizeSimulationType(rawName);
  const simulationAttacks = Array.isArray(simulationType?.simulation_attacks)
    ? simulationType.simulation_attacks
    : [];

  return {
    id: simulationType?.id ?? null,
    value: name,
    label: simulationType?.label || name,
    attackTypeOptions: dedupeAttackTypeOptions(
      simulationAttacks.map(normalizeAttackTypeOption).filter(Boolean)
    ),
  };
}

export function getSelectedAttackTypeOption(data, attackTypeOptions = []) {
  const attackType = normalizeAttackType(data.attackVector || data.attackType);
  return attackTypeOptions.find((option) => normalizeAttackType(option.value) === attackType) || null;
}

export function getEffectiveAttackFlowType(data, attackTypeConditions = {}, attackTypeOption = null) {
  const rules = getAttackTypeRules(data, attackTypeConditions);
  return normalizeFlowType(rules.type || attackTypeOption?.type);
}

export function applyAttackVectorDefaults(data, attackVector) {
  const nextAttackVector = normalizeAttackType(attackVector);
  const previousAttackVector = normalizeAttackType(data.attackVector);
  const next = { ...data, attackVector: nextAttackVector };

  if (nextAttackVector === "Vishing" && previousAttackVector !== "Vishing") {
    next.targetInteractionType = "outbound";
    next.isAIbased = false;
  }

  if (nextAttackVector === "WhatsApp" && previousAttackVector !== "WhatsApp") {
    next.targetInteractionType = "outbound";
    next.isAIbased = false;
  }

  next.targetInteractionType = normalizeInteractionType(next.targetInteractionType);
  return next;
}

export function isAiWhatsappFlow(data) {
  const attackType = normalizeAttackType(data.attackVector || data.attackType);
  return attackType === "WhatsApp" && normalizeInteractionType(data.targetInteractionType) === "inbound" && coerceBoolean(data.isAIbased, false);
}

export function isInboundVishingFlow(data) {
  const attackType = normalizeAttackType(data.attackVector || data.attackType);
  return (attackType === "Vishing" && normalizeInteractionType(data.targetInteractionType) === "inbound");
}

export function isOutboundVishingFlow(data) {
  const attackType = normalizeAttackType(data.attackVector || data.attackType);
  if (attackType !== "Vishing") return false;
  const targetInteractionType = normalizeInteractionType(data.targetInteractionType);
  return targetInteractionType === "outbound" || (!targetInteractionType && !coerceBoolean(data.isAIbased, false));
}

export function isEmailTemplateFlow(data, attackTypeConditions = {}, attackTypeOption = null) {
  return isEmailFlowType(getEffectiveAttackFlowType(data, attackTypeConditions, attackTypeOption));
}

export function isMessageTemplateFlow(data, attackTypeConditions = {}, attackTypeOption = null) {
  return isMessageFlowType(getEffectiveAttackFlowType(data, attackTypeConditions, attackTypeOption));
}

export function shouldShowTemplatePreview(data, attackTypeConditions = {}, attackTypeOption = null) {
  if (isOutboundVishingFlow(data)) return false;
  const rules = getAttackTypeRules(data, attackTypeConditions);
  if (rules.templateId === false) return false;
  return isEmailTemplateFlow(data, attackTypeConditions, attackTypeOption);
}

export function shouldShowLandingPageTab(data, attackTypeConditions = {}) {
  const attackType = normalizeAttackType(data.attackVector || data.attackType);
  if (
    attackType === "Attachment" ||
    attackType === "Removal Media" ||
    isOutboundVishingFlow(data) ||
    isInboundVishingFlow(data) ||
    isAiWhatsappFlow(data)
  ) {
    return false;
  }

  const rules = getAttackTypeRules(data, attackTypeConditions);
  return !!rules.isLandingPageValid && coerceBoolean(data.credentialHarvesting, false);
}

export function shouldShowAwarenessPageTab(data, attackTypeConditions = {}) {
  const attackType = normalizeAttackType(data.attackVector || data.attackType);
  if (
    attackType === "Ransomware" ||
    attackType === "Removal Media" ||
    isOutboundVishingFlow(data) ||
    isInboundVishingFlow(data) ||
    isAiWhatsappFlow(data)
  ) {
    return false;
  }

  const rules = getAttackTypeRules(data, attackTypeConditions);
  return !!rules.awarenessPage;
}

export function getEffectiveAttackTypeKey(data) {
  const attackType = normalizeAttackType(data.attackVector || data.attackType);
  const targetInteractionType = normalizeInteractionType(data.targetInteractionType);

  if (attackType === "Vishing" && targetInteractionType === "inbound") {
    return "Vishing-inbound";
  }
  if (attackType === "WhatsApp" && targetInteractionType === "inbound" && coerceBoolean(data.isAIbased, false)) {
    return "WhatsApp-inbound";
  }
  return attackType;
}

export function getAttackTypeRules(data, attackTypeConditions = {}) {
  return attackTypeConditions[getEffectiveAttackTypeKey(data)] || attackTypeConditions[normalizeAttackType(data.attackVector)] || {};
}

export function getAwarenessTargetOptions(data) {
  if (normalizeAttackType(data.attackVector) === "Attachment") {
    return [{ value: "Attachment Opened", label: "Attachment Opened" }];
  }

  const options = [{ value: "linkclicked", label: "Link Clicked" }];
  if (coerceBoolean(data.credentialHarvesting, false)) {
    options.push({ value: "datasubmitted", label: "Data Submitted" });
  }
  return options;
}

export function generateIdentifier() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function getTemplateIds(payload = {}) {
  const ids = [
    ...toNumberArray(payload.campaignTemplateIds),
    ...toNumberArray(payload.campaignTemplateId),
  ];
  return [...new Set(ids)];
}

export function buildImageHtml(src, alt) {
  if (!src) return "";
  return `<p><img src="${src}" alt="${alt}" style="max-width:100%;height:auto;border-radius:8px;" /></p>`;
}

export function sumGroupTargets(groups) {
  return groups.reduce((total, group) => total + (Number(group.targets) || 0), 0);
}

export function normalizeGroupRows(groupIds, groups) {
  const rowsById = new Map((groups || []).map((group) => [Number(group.id), group]));
  return groupIds.map((id) => {
    const group = rowsById.get(Number(id));
    return {
      id,
      name: group?.name || `Group ${id}`,
      targets: Number(group?.targets) || 0,
    };
  });
}

export function getTemplatePreviewFallback(attackType, templateName) {
  if (["Smishing", "WhatsApp", "Telegram"].includes(attackType)) {
    return `<p>${templateName || "Message template preview is not available."}</p>`;
  }
  return "<p>Email template preview is not available.</p>";
}

// Fields that belong to an individual campaign template (one per template tab).
// Everything else on `data` is campaign-level and shared across templates.
export const TEMPLATE_CONTENT_KEYS = [
  "subject",
  "senderName",
  "body",
  "landingPageHtml",
  "landingPageUrl",
  "awarenessHtml",
];

export function pickTemplateContent(source = {}) {
  return TEMPLATE_CONTENT_KEYS.reduce((content, key) => {
    content[key] = source[key];
    return content;
  }, {});
}

export function pickTemplateDetail(templateDetails, templateId, index) {
  const list = Array.isArray(templateDetails) ? templateDetails : [];
  const matched = list.find((detail) => {
    const campaignTemplate = detail?.campaign_template || {};
    return toNumber(campaignTemplate.id) === toNumber(templateId) || toNumber(detail?.id) === toNumber(templateId);
  });
  return matched || list[index] || {};
}

export function buildTemplateContent({ templateId, payload, templateDetail, templatePreview, awarenessPageData, attackType }) {
  const detailCampaignTemplate = templateDetail?.campaign_template || {};
  const previewCampaignTemplate = templatePreview?.campaign_template || {};
  const template = detailCampaignTemplate.template || previewCampaignTemplate.template || {};
  const campaignTemplateName = detailCampaignTemplate.name || previewCampaignTemplate.name || "";
  const subject = template.subject || campaignTemplateName || payload.campaignName || "";
  const body = template.html || getTemplatePreviewFallback(attackType, template.templateName || campaignTemplateName);
  const senderAddress = previewCampaignTemplate.sender?.from_address || template.brodcastName || "";
  const page = detailCampaignTemplate.page || previewCampaignTemplate.page || {};
  const detailAwarenessPage =
    (detailCampaignTemplate.awareness_pages && detailCampaignTemplate.awareness_pages[0]) ||
    (previewCampaignTemplate.awareness_pages && previewCampaignTemplate.awareness_pages[0]) ||
    {};

  return {
    id: templateId,
    name: campaignTemplateName || `Template ${templateId}`,
    thumbnail: template.thumbnail,
    thumbnailUrl: template.thumbnailUrl,
    subject,
    senderName: senderAddress || "Campaign Sender",
    body,
    landingPageHtml: page.html || buildImageHtml(templateDetail?.pagePath || templatePreview?.pagePath, "Landing Page"),
    landingPageUrl: page.landing_url || "",
    awarenessHtml:
      awarenessPageData?.html ||
      detailAwarenessPage.html ||
      buildImageHtml(templateDetail?.awarenessPath || templatePreview?.awarenessPath, "Awareness Page"),
    // Backend identifiers used when persisting inline edits (one per template tab).
    emailTemplateId: toNumber(detailCampaignTemplate.templateId ?? template.id ?? previewCampaignTemplate.template?.id),
    landingPageId: toNumber(detailCampaignTemplate.pageId ?? page.id ?? previewCampaignTemplate.page?.id),
    landingRedirectUrl: page.redirect_url || "",
    capturePasswords: coerceBoolean(page.capture_passwords, false),
    captureCredentials: coerceBoolean(page.capture_credentials, false),
    awarenessPageId: toNumber(awarenessPageData?.id ?? detailAwarenessPage.id),
    awarenessMode: awarenessPageData?.mode || detailAwarenessPage.mode || "editor",
    awarenessCustomUrl:
      awarenessPageData?.mode === "custom-url"
        ? awarenessPageData?.url || ""
        : detailAwarenessPage.mode === "custom-url"
        ? detailAwarenessPage.url || ""
        : "",
  };
}

export function buildDataFromSources(payload, groups, templateDetails, templatePreviews = [], awarenessPages = []) {
  const scheduledAt = toDate(payload.scheduledAt);
  const expiry = resolveExpiryState(scheduledAt, payload.campaignExpiry, payload.expiryOption);
  const groupIds = toNumberArray(payload.groupIds);
  const groupDetails = normalizeGroupRows(groupIds, groups);
  const templateIds = getTemplateIds(payload);
  const previewCampaignTemplate = templatePreviews[0]?.campaign_template || {};
  const attackType = normalizeAttackType(payload.attackType || previewCampaignTemplate.attack_type?.name);
  const simulationType = normalizeSimulationType(payload.simulationType || previewCampaignTemplate.simulation_type?.name);

  const templates = templateIds.map((templateId, index) =>
    buildTemplateContent({
      templateId,
      payload,
      templateDetail: pickTemplateDetail(templateDetails, templateId, index),
      templatePreview: templatePreviews[index] || {},
      awarenessPageData: awarenessPages[index] || {},
      attackType,
    })
  );
  const firstTemplate = templates[0] || pickTemplateContent({});

  const firstTemplateDetail = pickTemplateDetail(templateDetails, templateIds[0], 0);
  const detailCampaignTemplate = firstTemplateDetail.campaign_template || {};
  const courseIds = toNumberArray([
    ...templatePreviews.flatMap((preview) => collectTemplateCourseIds(preview?.campaign_template)),
    ...templateDetails.flatMap((detail) => collectTemplateCourseIds(detail?.campaign_template)),
  ]);
  const groupTags = groupDetails.map((group) => `${group.name} ( ${group.targets} Members )`);
  const isAIbased = coerceBoolean(payload.isAIbased ?? previewCampaignTemplate.isAIbased, false);
  const normalizedTargetInteractionType = normalizeInteractionType(payload.targetInteractionType || previewCampaignTemplate.targetInteractionType);
  const targetInteractionType = normalizedTargetInteractionType ||
    (attackType === "AI Vishing" ? "inbound" : ["Vishing", "WhatsApp"].includes(attackType) ? (isAIbased ? "inbound" : "outbound") : "");

  const data = {
    ...emptyData,
    ...expiry,
    name: payload.campaignName || "",
    attackVector: attackType,
    type: simulationType,
    users: String(sumGroupTargets(groupDetails)), //Currenlty it is summing all the users sum of the groups whihc is wrong as there will be some of the common users in the groups which shouldn't have been counted
    groups: String(groupDetails.length || groupIds.length),
    groupIds,
    groupDetails,
    groupTags,
    ...pickTemplateContent(firstTemplate),
    senderTo: groupTags.length ? groupTags.join(", ") : "Selected groups",
    campaignPreviewId: payload.campaignPreviewId || "",
    campaignTemplateId: templateIds[0] || null,
    campaignTemplateIds: templateIds,
    category: payload.category || previewCampaignTemplate.category?.id || "",
    single: payload.single !== undefined ? !!payload.single : templateIds.length <= 1,
    random: payload.random !== undefined ? !!payload.random : templateIds.length > 1,
    scheduledAt,
    executionMode: payload.campaignType === "test" ? "Test" : (scheduledAt && new Date(scheduledAt) > new Date())? "Schedule for later": "Immediately",
    campaignType: payload.campaignType || "live",
    identifier: payload.identifier || generateIdentifier(),
    credentialHarvesting: coerceBoolean(
      payload.credentialHarvesting ?? detailCampaignTemplate.credential_harvesting ?? previewCampaignTemplate.credential_harvesting,
      false
    ),
    targetInteractionType,
    isAIbased,
    sendCampaignOnOldSimulation: coerceBoolean(payload.sendCampaignOnOldSimulation, false),
    simulationId: toNumber(payload.simulationId) || 0,
    simulationState: payload.simulationState || payload.simulationState1 || null,
    simulationState1: payload.simulationState1 || payload.simulationState || null,
    simulationState2: payload.simulationState2 || null,
    lmsMsgStatus: payload.lmsMsgStatus || "",
    delay: payload.delay || 1,
    isRepliedCampaign: coerceBoolean(payload.isRepliedCampaign, false),
    isInstantAwareness: coerceBoolean(payload.isInstantAwareness, true),
    launchStatus: payload.launchStatus || "draft",
    launchedCampaignId: payload.launchedCampaignId || null,
    courseIds,
  };

  return { data, templates };
}

export function resolveCampaignExpiry(data, scheduledAt) {
  if (data.expiryOption === "Custom") {
    return formatDateTime(data.customExpiryDate || data.campaignExpiry);
  }
  const expiryOption = normalizeExpiryOption(data.expiryOption) || "7 Days";
  return formatDateTime(addDays(scheduledAt, EXPIRY_OPTION_DAYS[expiryOption] || 7));
}

export function resolveLmsMsgStatus(data) {
  if (data.type !== "Simulation Attack + Training") return data.lmsMsgStatus || "";
  if (data.lmsMsgStatus) return data.lmsMsgStatus;
  if (data.attackVector === "Attachment") return "Attachment Opened";
  return data.credentialHarvesting ? "datasubmitted" : "linkclicked";
}

export function cleanPayload(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
}
